import React, {useContext, useEffect, useState} from "react";
import {Image, Pressable} from "react-native";
import MainStyle from "../../MainStyle";
import lock_icon from '../assets/img/icons/icon-lock-small.svg';
import {outline_0} from "../constants";
import {colors} from "../assets/styles/colors";
import {
  Tournament, TournamentPlayerBet_State, TournamentPlayerDetails_State, TournamentState
} from "../../generated/smg_service_pb";
import {Ionicons} from '@expo/vector-icons';
import TournamentRegistrationConfirmationModal from "../modals/TournamentRegistrationConfirmationModal";
import ServiceClient from "../utils/ServiceClient";
import {StoreContext} from "./StoreContextProvider";
import {useNavigation} from "@react-navigation/native";
import {formatCurrency} from "../utils";
import Div from "../lib/Div";
import P from "../lib/P";
import {track} from "../services/analytics";

type Props = {
  item: Tournament, mb?: string | number, onReload: () => void, isPrivateTournament?: boolean,
  showPopup?: boolean
};

const TournamentHeader = (props: Props) => {
  const {item, mb, isPrivateTournament, showPopup} = props;
  const [isOpen, setIsOpen] = useState(false);
  const Context = useContext(StoreContext);
  const {cancelModalConfig} = Context;
  const navigation: any = useNavigation();

  const updateSoloChallengeAmount = async (fee: string) => {
    const amount = fee === '-2' ? -2 : +fee * 100;
    const playingBets = item.currentPlayerDetails?.bets.filter(bet => bet.state === TournamentPlayerBet_State.PLAYING) ?? [];
    const currentBet = playingBets[0];

    if (playingBets.length > 1) {
      console.warn('Multiple PLAYING tournament bets found', {
        tournamentId: item.id,
        playingBetIds: playingBets.map(bet => bet.betId),
      });
      track('Multiple PLAYING tournament bets detected', {
        tournamentId: item.id,
        playingBetCount: playingBets.length,
        source: 'TournamentHeader.updateSoloChallengeAmount',
      });
    }

    Context.setLoadingOverlay(true);
    setIsOpen(false);
    navigation.navigate('TournamentDetails', {id: item.id, isPrivateTournament});

    try {
      if (currentBet) {
        await ServiceClient.adjustBet(BigInt(currentBet.betId), amount);
      }

      await ServiceClient.enableAutoPlay(true, amount);
      props.onReload();

    } catch (err: any) {
      console.log('updateSoloChallengeAmount err', err);

      Context.setCancelModalConfig({
        visible: true, title: 'Error', subtitle: err.message, rightBtnText: 'OK',
        onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
      });

    } finally {
      Context.setLoadingOverlay(false);
    }
  };

  useEffect(() => {
    const isCancelled = item.state === TournamentState.FINISHED || item.state === TournamentState.CANCELLED || item.state === TournamentState.VOIDED;
    if (isPrivateTournament && item.id && !isCancelled && showPopup) {
      setIsOpen(true);
    }
  }, [isPrivateTournament, item.id, showPopup]);

  return (
    <Div mx="auto" w={367}>
      <Div row>
        <P fw="500" fs={14} flex={1} ta="center" tt="uppercase" lh={18}>Players</P>
        <P fw="500" fs={14} flex={1} ta="center" tt="uppercase" lh={18}>Registered</P>
        <P fw="500" fs={14} flex={1} ta="center" tt="uppercase" lh={18}>Availability</P>
      </Div>

      <Div row mb={8}>
        <P fw="700" lh={18} flex={1} ta="center">1 - {item.maxParticipants}</P>
        <P fw="700" lh={18} flex={1} ta="center">{item.playerDetails.length}</P>
        <Div flex={1} alignItems="center">
          <Div px={5} bg={colors.availability} row alignItems="center">
            <P fw="700" fs={14} lh={18} tt="capitalize">{item.isPrivate ? 'PRIVATE' : 'PUBLIC'}</P>
            <Image source={lock_icon as any} style={[MainStyle.w_8, MainStyle.h_12, MainStyle.m_l_4]}/>
          </Div>
        </Div>
      </Div>

      <Div row>
        <P fw="500" fs={14} flex={1} ta="center" tt="uppercase" lh={18}>total Shots</P>
        <P fw="500" fs={14} flex={1} ta="center" tt="uppercase" lh={18}>Entry Fee</P>
        <P fw="500" fs={14} flex={1} ta="center" tt="uppercase" lh={18}>Solo Challenge</P>
      </Div>

      <Div row mb={mb}>
        <P fw="700" lh={18} flex={1} ta="center">{item.totalShots}</P>
        <P fw="700" lh={18} flex={1} ta="center">{formatCurrency(item.entryFee / 100)}</P>

        <Div flex={1} alignItems="center">
          {!!item.currentPlayerDetails && item.currentPlayerDetails.state !== TournamentPlayerDetails_State.FINISHED ? (
            <Pressable style={styles.input_wrap} onPress={() => setIsOpen(true)}>
              <P fw="500" fs={15} color={colors.newGreenText}>
                {item.currentPlayerDetails.betEntreeFee ? formatCurrency(item.currentPlayerDetails.betEntreeFee / 100) : '$0'}
              </P>

              <Ionicons
                name="pencil" size={11} color={colors.newGreenText}
                style={[MainStyle.absolute, MainStyle.r__17]}
              />
            </Pressable>
          ) : (
            <P fw="700" lh={18} flex={1} ta="center">
              {item.currentPlayerDetails?.betEntreeFee ? formatCurrency(item.currentPlayerDetails.betEntreeFee / 100) : '$0'}
            </P>
          )}
        </Div>
      </Div>

      <TournamentRegistrationConfirmationModal
        isOpen={isOpen} joinTournament={updateSoloChallengeAmount} type="ChallengeFeeConfirmationModal"
        entryChallengeFee={`${item.currentPlayerDetails?.betEntreeFee ? item.currentPlayerDetails.betEntreeFee / 100 : 0}`}
        totalShots={item.totalShots} entryFee={item.entryFee}
        onClose={() => {
          setIsOpen(false);
          navigation.navigate('TournamentDetails', {id: item.id, isPrivateTournament});
        }}
      />
    </Div>
  );
};

const styles = {
  input_s: [MainStyle.absolute, MainStyle.t_0, MainStyle.r_0, MainStyle.b_0, MainStyle.l_18, outline_0],
  input_wrap: [
    MainStyle.row, MainStyle.relative, MainStyle.b_b_w_1, MainStyle.b_c_newGreen, MainStyle.p_x_8, MainStyle.a_i_c
  ]
};

export default React.memo(TournamentHeader);
